import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { CirclePhoto } from '../../components';
import { ElectionMigrationEntity } from '../../app/feathers/election-migration-runs/ElectionMigrationRun';

export { toggleWinnerSelection } from './CandidateWinnerRows.util';

const MAX_CANDIDATES_VISIBLE = 5;

/**
 * Shaded circle colors aligned with `ColorTag` / `PartyTag` (see components/lower-order/PartyTag + ColorTag).
 */
const PARTY_TAG_CIRCLE_COLORS: Record<string, { backgroundColor: string; color: string }> = {
  R: { backgroundColor: '#FF9A9A', color: '#972922' },
  D: { backgroundColor: '#A9C9FF', color: '#453DB7' },
  G: { backgroundColor: '#99d266', color: '#0A584C' },
  L: { backgroundColor: '#F2F4C4', color: '#7A733C' },
  I: { backgroundColor: '#EEEEEE', color: '#333333' },
  N: { backgroundColor: '#EEEEEE', color: '#333333' }
};

function partyLetterCircleSx(partyRaw: string) {
  const key = partyRaw.trim().charAt(0).toUpperCase();
  return PARTY_TAG_CIRCLE_COLORS[key] || { backgroundColor: '#EEEEEE', color: '#333333' };
}

function PartyLetterCircle({ party }: { party: string }) {
  const letter = party.trim().charAt(0).toUpperCase() || '?';
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        userSelect: 'none',
        letterSpacing: 0,
        ...partyLetterCircleSx(party)
      }}
    >
      {letter}
    </Box>
  );
}

type MergedCandidateRow = {
  candidateId: string;
  sourceRaceId: string;
  name: string;
  photo?: string;
  hasVariants?: boolean;
  party?: string;
};

type PrimaryPrecedentEntry = {
  _id: string;
  maxChoices?: number;
  winners?: { _id?: string }[];
};

function candidateCountForSourceRace(entity: ElectionMigrationEntity, raceId: string): number {
  const rid = `${raceId}`;
  for (const row of entity.sourceCandidateSummaries || []) {
    if (`${row.raceId}` !== rid) {
      continue;
    }
    const s = row.candidateSummary || {};
    return Math.max(
      s.numCandidates ?? 0,
      (s.ids || []).length,
      (s.names || []).length
    );
  }
  return 0;
}

/** Winners required for one primary source race: maxChoices, capped by how many candidates exist. */
function expectedWinnersForPrimaryEntry(entity: ElectionMigrationEntity, entry: PrimaryPrecedentEntry): number {
  const raw = Number(entry.maxChoices);
  const maxC = Number.isFinite(raw) && raw >= 1 ? raw : 1;
  const n = candidateCountForSourceRace(entity, entry._id);
  if (n <= 0) {
    return 0;
  }
  return Math.min(maxC, n);
}

function sourceRaceDisplayName(entity: ElectionMigrationEntity, raceId: string): string {
  const rid = `${raceId}`;
  const ids = entity.sourceRaceIds || [];
  const names = entity.sourceRaceNames || [];
  const idx = ids.findIndex((id) => `${id}` === rid);
  if (idx >= 0 && names[idx]) {
    return names[idx];
  }
  return rid;
}

function countWinnersInPrimaryEntry(entry: PrimaryPrecedentEntry): number {
  return (entry.winners || []).filter((w) => w && w._id != null && `${w._id}` !== '').length;
}

/**
 * True when at least one source race needs more winner selections (expected = min(maxChoices, candidate count)).
 */
export function isEntityWinnerSelectionIncomplete(entity: ElectionMigrationEntity): boolean {
  return getWinnerSelectionShortfallLines(entity).length > 0;
}

/** Human-readable lines for tooltip (one per shortfall). */
export function getWinnerSelectionShortfallLines(entity: ElectionMigrationEntity): string[] {
  const lines: string[] = [];

  if (entity.migrationKind === 'primary-to-general') {
    const pp = entity.createPayload?.primaryPrecedent as PrimaryPrecedentEntry[] | undefined;
    if (!Array.isArray(pp)) {
      return lines;
    }
    for (const entry of pp) {
      if (!entry?._id) {
        continue;
      }
      const expected = expectedWinnersForPrimaryEntry(entity, entry);
      if (expected <= 0) {
        continue;
      }
      const have = countWinnersInPrimaryEntry(entry);
      if (have < expected) {
        const label = sourceRaceDisplayName(entity, entry._id);
        lines.push(`${label}: ${have} of ${expected} winner(s) selected (max choices ${entry.maxChoices ?? 1})`);
      }
    }
    return lines;
  }

  if (entity.migrationKind === 'to-runoff') {
    const expected = 2;
    const raceId = entity.sourceRaceIds?.[0];
    const rp = entity.createPayload?.runoffPrecedent;
    const have =
      rp && typeof rp === 'object' && Array.isArray((rp as { winners?: { _id?: string }[] }).winners)
        ? (rp as { winners: { _id?: string }[] }).winners.filter(
            (w) => w && w._id != null && `${w._id}` !== ''
          ).length
        : 0;
    if (have < expected) {
      const label = raceId ? sourceRaceDisplayName(entity, raceId) : 'Runoff source race';
      lines.push(`${label}: ${have} of ${expected} winner(s) selected`);
    }
  }

  return lines;
}

export function getSelectedCandidateIdsFromEntity(entity: ElectionMigrationEntity): string[] {
  if (entity.migrationKind === 'primary-to-general') {
    const pp = entity.createPayload?.primaryPrecedent;
    if (!Array.isArray(pp)) {
      return [];
    }
    const out: string[] = [];
    for (const entry of pp) {
      for (const w of entry.winners || []) {
        const id = w?._id;
        if (id) {
          out.push(`${id}`);
        }
      }
    }
    return out;
  }
  const rp = entity.createPayload?.runoffPrecedent;
  if (rp && typeof rp === 'object' && Array.isArray(rp.winners)) {
    return rp.winners.map((w: { _id?: string }) => `${w._id ?? w}`);
  }
  return [];
}

function mergeEntityCandidateRows(entity: ElectionMigrationEntity): MergedCandidateRow[] {
  const rows: MergedCandidateRow[] = [];
  for (const { raceId, candidateSummary } of entity.sourceCandidateSummaries || []) {
    const s = candidateSummary || {};
    const n = Math.max(
      s.numCandidates ?? 0,
      (s.ids || []).length,
      (s.names || []).length
    );
    for (let i = 0; i < n; i += 1) {
      const cid = s.ids?.[i];
      if (!cid) {
        continue;
      }
      rows.push({
        candidateId: `${cid}`,
        sourceRaceId: `${raceId}`,
        name: s.names?.[i] || 'Unknown',
        photo: s.photos?.[i],
        hasVariants: s.photoHasVariants?.[i] === true,
        party: s.parties?.[i]
      });
    }
  }
  const seen = new Set<string>();
  return rows.filter((r) => {
    if (seen.has(r.candidateId)) {
      return false;
    }
    seen.add(r.candidateId);
    return true;
  });
}

function sortCandidatesForDisplay(rows: MergedCandidateRow[], selectedOrdered: string[]): MergedCandidateRow[] {
  const rank = (id: string) => {
    const i = selectedOrdered.indexOf(id);
    if (i === -1) {
      return 1000 + rows.findIndex((r) => r.candidateId === id);
    }
    return i;
  };
  return [...rows].sort((a, b) => rank(a.candidateId) - rank(b.candidateId));
}

export type WinnersPickerCellProps = {
  entity: ElectionMigrationEntity;
  canEdit: boolean;
  patching: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleCandidate: (candidateId: string) => void;
};

export function WinnersPickerCell({
  entity,
  canEdit,
  patching,
  expanded,
  onToggleExpand,
  onToggleCandidate
}: WinnersPickerCellProps) {
  const selectedOrdered = getSelectedCandidateIdsFromEntity(entity);
  const merged = mergeEntityCandidateRows(entity);
  const sorted = sortCandidatesForDisplay(merged, selectedOrdered);
  const limit = expanded ? sorted.length : MAX_CANDIDATES_VISIBLE;
  const visible = sorted.slice(0, limit);

  if (!entity.sourceCandidateSummaries?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Refresh discovery to load candidates (candidate summaries).
      </Typography>
    );
  }

  if (sorted.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No candidates on source races.
      </Typography>
    );
  }

  const winnerShortfalls = getWinnerSelectionShortfallLines(entity);
  const showWinnerWarning = winnerShortfalls.length > 0;
  const winnerWarningTitle = showWinnerWarning
    ? winnerShortfalls.length === 1
      ? winnerShortfalls[0]
      : ['Not enough winners selected:', ...winnerShortfalls.map((l) => `• ${l}`)].join('\n')
    : '';

  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ maxWidth: 360 }}>
      {showWinnerWarning ? (
        <Tooltip
          title={winnerWarningTitle}
          slotProps={{
            tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } }
          }}
        >
          <WarningAmberOutlinedIcon
            fontSize="small"
            color="warning"
            sx={{ flexShrink: 0, mt: 0.15 }}
            aria-label="Winner selection incomplete"
          />
        </Tooltip>
      ) : null}
      <Stack spacing={0} alignItems="flex-start" sx={{ maxWidth: 320, flex: 1, minWidth: 0 }}>
      {visible.map((row) => {
        const selected = selectedOrdered.includes(row.candidateId);
        return (
          <Box
            key={`${row.sourceRaceId}-${row.candidateId}`}
            onClick={() => {
              if (canEdit && !patching) {
                onToggleCandidate(row.candidateId);
              }
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              cursor: canEdit && !patching ? 'pointer' : 'default',
              opacity: patching ? 0.6 : 1
            }}
          >
            <CirclePhoto
              photoPath={row.photo}
              hasVariants={row.hasVariants === true}
              size="mini"
              checkmark={selected}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                flex: 1,
                minWidth: 0,
                marginLeft: '-8px',
                justifyContent: 'flex-start'
              }}
            >
              <Typography
                variant="caption"
                component="span"
                sx={{
                  lineHeight: 1.3,
                  fontWeight: selected ? 600 : 400
                }}
              >
                {row.name}
              </Typography>
              {row.party ? <PartyLetterCircle party={row.party} /> : null}
            </Box>
          </Box>
        );
      })}
      {sorted.length > MAX_CANDIDATES_VISIBLE ? (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={(e) => {
            e.preventDefault();
            onToggleExpand();
          }}
          sx={{ cursor: 'pointer' }}
        >
          {expanded ? 'Show fewer' : `Show all (${sorted.length})`}
        </Link>
      ) : null}
      </Stack>
    </Stack>
  );
}
